import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogClose,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Icon,
  Image,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Text,
  Video,
} from "@salestrak/ui";
import { VideoPlayBadge } from "#app/components/media/media-tiles";
import { useMediaDownloads } from "#app/lib/hooks/use-media-downloads";
import {
  useMediaActions,
  useMediaObjectUrl,
  useMediaStreamUrl,
} from "#app/lib/hooks/use-note-media";
import { fileIconName, hasThumbnail, mediaKind } from "#app/lib/media-kind";
import type { Omittable } from "@salestrak/ui";

/** A media item the lightbox can show: image (inline), video (player), or
 * anything else (PDF/doc → an Open button). */
export type LightboxMediaItem = Omittable<{
  id: string;
  contentType: string;
  fileName?: string | null;
}>;

// Max thumbnails mounted in the strip at once (windowed around the current
// index). Bounds the concurrent thumb fetches when opening a media-heavy issue.
const THUMB_STRIP_WINDOW = 24;

export type PhotoLightboxProps = Omittable<{
  /** Permission-gated media, in display order (image/video/pdf/doc). */
  media: LightboxMediaItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which photo to show first. Defaults to 0. */
  initialIndex?: number;
  /** Optional heading (field name, note context, …). */
  title?: string;
  /** Prefixes download filenames (e.g. the issue name):
   * `{prefix}-photo-2.jpg`, `{prefix}-{title}.zip`. */
  downloadPrefix?: string;
}>;

/**
 * Fullscreen photo viewer with carousel navigation — reusable for any
 * screen holding gated photo ids (custom fields, note attachments). Bytes
 * resolve through the authenticated content route.
 *
 * ONE Dialog at every breakpoint: Base UI coordinates nested layers natively,
 * so a lightbox opened from inside the issue-view sheet stacks above it — no
 * mobile fork, no stacking workaround.
 */
export function MediaLightbox({
  media,
  open,
  onOpenChange,
  initialIndex = 0,
  title,
  downloadPrefix,
}: PhotoLightboxProps) {
  const [index, setIndex] = useState(0);
  const count = media.length;

  // Re-anchor on every open (and clamp against the current list).
  useEffect(() => {
    if (!open) return;
    setIndex(Math.min(Math.max(initialIndex, 0), Math.max(count - 1, 0)));
  }, [open, initialIndex, count]);

  const step = useCallback(
    (delta: number) => {
      if (count < 2) return;
      setIndex((current) => (current + delta + count) % count);
    },
    [count],
  );

  // Keyboard carousel — listeners live only while the viewer is open (desktop;
  // a no-op on touch, harmless to leave wired).
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") step(-1);
      else if (event.key === "ArrowRight") step(1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, step]);

  const current = media[index] ?? null;
  const currentId = current?.id ?? null;

  // The thumbnail strip mounts one ThumbnailTile per rendered id, and each tile
  // fetches its thumb on mount. Window it around the current index so opening a
  // media-heavy issue (hundreds of photos) doesn't fire hundreds of concurrent
  // thumb fetches at once — chevrons / arrow keys still traverse the full set,
  // and the window slides as the index moves.
  const stripStart =
    count <= THUMB_STRIP_WINDOW
      ? 0
      : Math.min(
          Math.max(index - Math.floor(THUMB_STRIP_WINDOW / 2), 0),
          count - THUMB_STRIP_WINDOW,
        );

  const { downloadPhoto, downloadAllPhotos, downloadingAll } =
    useMediaDownloads(downloadPrefix);

  const handleDownload = useCallback(async () => {
    if (!current) return;
    await downloadPhoto(current.id, index + 1, current.fileName);
  }, [downloadPhoto, current, index]);

  const handleDownloadAll = useCallback(async () => {
    await downloadAllPhotos(
      media.map((m) => ({ id: m.id, fileName: m.fileName })),
      title,
    );
  }, [downloadAllPhotos, media, title]);

  return (
    // Not responsive: fullscreen media.
    <Dialog responsive={false} open={open} onOpenChange={onOpenChange}>
      {/* The stock corner close button is folded into the control cluster —
          counter, chevrons, menu and close read as one toolbar. */}
      <DialogPopup size="full" showCloseButton={false}>
        <DialogHeader className="flex-row items-center justify-between gap-2">
          <DialogTitle>{title ?? "Photos"}</DialogTitle>
          <LightboxControls
            index={index}
            count={count}
            step={step}
            hasCurrent={!!currentId}
            downloadingAll={downloadingAll}
            onDownload={handleDownload}
            onDownloadAll={handleDownloadAll}
          />
        </DialogHeader>
        {/* The image box must STRETCH to the body height — centering the
            column would collapse it (and the image's 100% sizing with it). */}
        <Box
          fill="flex"
          minHeight={0}
          overflow="hidden"
          justify="center"
          paddingX="lg"
          // The strip owns the bottom gutter; without it, the body does.
          paddingBottom={count > 1 ? undefined : "lg"}
        >
          {current ? (
            <LightboxBody item={current} />
          ) : (
            <Box align="center">
              <Text variant="muted">No media</Text>
            </Box>
          )}
        </Box>
        {count > 1 ? (
          <Box
            direction="row"
            wrap
            justify="center"
            align="center"
            gap="sm"
            paddingX="lg"
            paddingTop="xs"
            paddingBottom="md"
          >
            <LightboxStrip
              media={media}
              stripStart={stripStart}
              index={index}
              onSelect={setIndex}
            />
          </Box>
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}

// Position counter + carousel chevrons + the download menu + close.
function LightboxControls({
  index,
  count,
  step,
  hasCurrent,
  downloadingAll,
  onDownload,
  onDownloadAll,
}: {
  index: number;
  count: number;
  step: (delta: number) => void;
  hasCurrent: boolean;
  downloadingAll: boolean;
  onDownload: () => void;
  onDownloadAll: () => void;
}) {
  return (
    <Box direction="row" align="center" gap="2xs">
      {count > 1 ? (
        <Box paddingRight="2xs">
          <Text size="sm" variant="muted">
            {`${index + 1} of ${count}`}
          </Text>
        </Box>
      ) : null}
      <Button
        variant="ghost"
        size="icon-sm"
        disabled={count < 2}
        onClick={() => step(-1)}
        aria-label="Previous photo"
      >
        <Icon name="chevron-left" size="md" />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        disabled={count < 2}
        onClick={() => step(1)}
        aria-label="Next photo"
      >
        <Icon name="chevron-right" size="md" />
      </Button>
      <Menu>
        <MenuTrigger
          render={
            <Button variant="ghost" size="icon-sm" aria-label="More options" />
          }
        >
          <Icon name="ellipsis" size="md" />
        </MenuTrigger>
        <MenuPopup align="end">
          <MenuItem onClick={onDownload} disabled={!hasCurrent}>
            <Icon name="download" />
            Download
          </MenuItem>
          <MenuItem
            onClick={onDownloadAll}
            disabled={count === 0 || downloadingAll}
          >
            <Icon name="download" />
            {downloadingAll ? "Preparing zip..." : "Download all"}
          </MenuItem>
        </MenuPopup>
      </Menu>
      <DialogClose
        aria-label="Close"
        render={<Button size="icon-sm" color="neutral" />}
      >
        <Icon name="x" />
      </DialogClose>
    </Box>
  );
}

// The windowed thumbnail strip tiles.
function LightboxStrip({
  media,
  stripStart,
  index,
  onSelect,
}: {
  media: LightboxMediaItem[];
  stripStart: number;
  index: number;
  onSelect: (index: number) => void;
}) {
  return (
    <>
      {media
        .slice(stripStart, stripStart + THUMB_STRIP_WINDOW)
        .map((item, offset) => {
          const i = stripStart + offset;
          return (
            <ThumbnailTile
              key={`${item.id}-${i}`}
              item={item}
              position={i + 1}
              selected={i === index}
              onClick={() => onSelect(i)}
            />
          );
        })}
    </>
  );
}

// Strip thumbnail — the selected item carries a primary outline; the rest dim
// until hovered. Image/PDF/video show their thumbnail (video gets a play
// badge); documents (no thumb) show a file icon.
function ThumbnailTile({
  item,
  position,
  selected,
  onClick,
}: {
  item: LightboxMediaItem;
  position: number;
  selected: boolean;
  onClick: () => void;
}) {
  const kind = mediaKind(item.contentType);
  const hasThumb = hasThumbnail(item.contentType);
  const uri = useMediaObjectUrl(hasThumb ? item.id : "", "thumb");
  return (
    <Box
      as="button"
      position="relative"
      width={48}
      height={48}
      rounded="md"
      overflow="hidden"
      borderColor={selected ? "primary" : "border"}
      surface="card"
      align="center"
      justify="center"
      onClick={onClick}
      aria-label={`Go to item ${position}`}
      className={selected ? "opacity-100" : "opacity-60 hover:border-primary"}
    >
      {hasThumb && uri ? (
        <Image src={uri} fill fit="cover" alt="" />
      ) : !hasThumb ? (
        <Icon name={fileIconName(item.contentType)} size="sm" color="muted" />
      ) : null}
      {kind === "video" ? <VideoPlayBadge size="sm" /> : null}
    </Box>
  );
}

// Main viewer body, by media class: image inline, video player, or an Open
// button for PDFs/documents (which view/download in a new tab).
function LightboxBody({ item }: { item: LightboxMediaItem }) {
  const kind = mediaKind(item.contentType);
  if (kind === "image") return <LightboxImage photoId={item.id} />;
  if (kind === "video")
    return <LightboxVideo photoId={item.id} fileName={item.fileName} />;
  return <LightboxOpen item={item} />;
}

function LightboxImage({ photoId }: { photoId: string }) {
  const uri = useMediaObjectUrl(photoId);
  if (!uri) return null;
  return <Image src={uri} fill fit="contain" alt="Photo" />;
}

function LightboxVideo({
  photoId,
  fileName,
}: Omittable<{
  photoId: string;
  fileName?: string | null;
}>) {
  const uri = useMediaStreamUrl(photoId);
  if (!uri) return null;
  return <Video src={uri} fill aria-label={fileName ?? "Video"} />;
}

function LightboxOpen({ item }: { item: LightboxMediaItem }) {
  const { openMedia } = useMediaActions();
  return (
    <Box fill="flex" align="center" justify="center" gap="md" padding="lg">
      <Icon name={fileIconName(item.contentType)} size="2xl" color="muted" />
      {item.fileName ? (
        <Text variant="muted" align="center">
          {item.fileName}
        </Text>
      ) : null}
      <Button onClick={() => openMedia(item.id)}>
        <Icon name="external-link" size="sm" />
        Open
      </Button>
    </Box>
  );
}
